import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import { PAGE_ALIAS_KEYS, type PageAliasKey } from '../config/pageAliasKeys'

export type PageAliasMap = Record<PageAliasKey, string[]>

/**
 * Returns localized search aliases for every page key in `PAGE_ALIAS_KEYS`.
 *
 * The locale stores a single comma-separated string per key under
 * `quickSearch.aliases.<key>` (e.g. `"evse, charging stations"`). We split and
 * trim at runtime so translators only see plain prose. Iterating the literal
 * `PAGE_ALIAS_KEYS` tuple keeps `t(key)` strongly typed — TS verifies every
 * entry against the `Messages` augmentation at compile time, so a missing key
 * fails the build.
 */
export default function usePageAliases(): PageAliasMap {
  const t = useTranslations('quickSearch.aliases')

  return useMemo(() => {
    const out = {} as PageAliasMap
    for (const key of PAGE_ALIAS_KEYS) {
      const raw = t(key)
      out[key] = raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    }
    return out
  }, [t])
}
